import { useTranslation } from 'react-i18next';

import { FertWorkDetailRecord } from '@/app/services/fertWork';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export type RemoveDonorModalProps = {
  visible: boolean;
  donorRecord: FertWorkDetailRecord | null;
  onHide: () => void;
  onConfirm: (donorId: string) => void;
};

export const RemoveDonorModal = ({
  visible,
  donorRecord,
  onHide,
  onConfirm,
}: RemoveDonorModalProps) => {
  const { t } = useTranslation();
  const handleConfirm = () => {
    if (donorRecord) {
      onConfirm(donorRecord.id);
      onHide();
    }
  };

  return (
    <Dialog
      open={visible}
      onOpenChange={(open) => {
        if (!open) onHide();
      }}
    >
      <DialogContent className="md:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t('fertWork.removeDonorRecord', 'Remove Donor Record')}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <i
              className="pi pi-exclamation-triangle text-orange-500"
              style={{ fontSize: '2rem' }}
            />
            <div>
              <p className="font-medium">
                {t(
                  'fertWork.removeConfirm',
                  'Are you sure you want to remove this donor record?',
                )}
              </p>
              <p className="mt-1 text-sm text-gray-600">
                {t('fertWork.cannotBeUndone', 'This action cannot be undone.')}
              </p>
            </div>
          </div>

          {donorRecord && (
            <div className="rounded border border-gray-200 bg-gray-50 p-3">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="font-medium">
                    {t('fertWork.donor', 'Donor')}:
                  </span>{' '}
                  {donorRecord.donorName || t('fertWork.na', 'N/A')}
                </div>
                <div>
                  <span className="font-medium">
                    {t('fertWork.sire', 'Sire')}:
                  </span>{' '}
                  {donorRecord.sireName || t('fertWork.na', 'N/A')}
                </div>
                <div>
                  <span className="font-medium">
                    {t('fertWork.totalOocytes', 'Total Oocytes')}:
                  </span>{' '}
                  {donorRecord.totalOocytes}
                </div>
                <div>
                  <span className="font-medium">
                    {t('fertWork.account', 'Account')}:
                  </span>{' '}
                  {donorRecord.accountName || t('fertWork.na', 'N/A')}
                </div>
              </div>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onHide}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button variant="destructive" onClick={handleConfirm}>
            {t('common.remove', 'Remove')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
